import { ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/language-context";
import { LanguageSwitcher } from "@/components/language-switcher";

export function Footer() {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="bg-[#f8f8f8] dark:bg-[#1a1a1a]">
      {/* Links Section */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
          {/* Get Started */}
          <div>
            <h4 className="font-medium mb-4 text-sm">{t.footer.getStarted || "Get Started"}</h4>
            <ul className="space-y-3">
              <li>
                <a href="#pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="link-footer-try-free">
                  {t.footer.tryFree || "Try 7-days free"}
                </a>
              </li>
              <li>
                <a href="#pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="link-footer-plans">
                  {t.footer.ourPlans || "Our plans"}
                </a>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-medium mb-4 text-sm">{t.footer.support}</h4>
            <ul className="space-y-3">
              <li>
                <a href="mailto:help@evooluir.com" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="link-footer-help">
                  {t.footer.helpCenter}
                </a>
              </li>
              <li>
                <a href="mailto:help@evooluir.com" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="link-footer-contact">
                  {t.footer.contact}
                </a>
              </li>
            </ul>
          </div>

          {/* Get the App */}
          <div>
            <h4 className="font-medium mb-4 text-sm">{t.footer.getApp || "Get the app"}</h4>
            <div className="space-y-3">
              <a href="https://play.google.com/store/apps/details?id=com.evooluir" target="_blank" rel="noopener noreferrer" className="inline-block" data-testid="link-footer-playstore">
                <div className="bg-black text-white rounded-[10px] px-4 py-2 flex items-center gap-2 hover:opacity-80 transition-opacity">
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 20.5v-17c0-.59.34-1.11.84-1.35L13.69 12l-9.85 9.85c-.5-.24-.84-.76-.84-1.35zm13.81-5.38L6.05 21.34l8.49-8.49 2.27 2.27zm3.35-4.31c.34.27.56.69.56 1.19 0 .5-.22.92-.56 1.19l-2.29 1.32-2.5-2.5 2.5-2.5 2.29 1.3zM6.05 2.66l10.76 6.22-2.27 2.27-8.49-8.49z"/>
                  </svg>
                  <div className="text-left">
                    <p className="text-[10px] leading-none">GET IT ON</p>
                    <p className="text-sm font-medium leading-tight">Google Play</p>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Scroll to top */}
        <div className="flex justify-end mt-8">
          <button 
            onClick={scrollToTop}
            className="p-2 rounded-full hover:bg-muted transition-colors"
            aria-label="Scroll to top"
            data-testid="button-scroll-top"
          >
            <ChevronUp className="w-6 h-6 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-[#101010] text-white py-4 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-4 gap-y-2 text-xs text-white/60">
            <span>© {currentYear} Evooluir</span>
            <a href="/terms" className="hover:text-white transition-colors" data-testid="link-footer-terms-bottom">
              {t.footer.terms}
            </a>
            <a href="/privacy" className="hover:text-white transition-colors" data-testid="link-footer-privacy-bottom">
              {t.footer.privacy}
            </a>
          </div>

          <div className="flex items-center gap-4">
            {/* Language Switcher */}
            <div className="ml-2">
              <LanguageSwitcher variant="footer" />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
